import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false);
  const servicesRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (servicesRef.current && !servicesRef.current.contains(e.target as Node)) {
        setIsServicesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
      setIsServicesOpen(false);
    }
  };

  const goHome = () => {
    navigate('/');
    setIsMobileMenuOpen(false);
  };

  const services = [
    { name: 'Climatisation', link: '/services/climatisation', icon: 'ri-temp-cold-line' },
    { name: 'Chauffage', link: '/services/chauffage', icon: 'ri-fire-line' },
    { name: 'Plomberie', link: '/services/plomberie', icon: 'ri-drop-line' },
    { name: 'Rénovation Salle de Bains', link: '/services/renovation-salle-de-bains', icon: 'ri-home-smile-line' },
    { name: 'Dépannage Urgent', link: '/services/depannage-urgent', icon: 'ri-alarm-warning-line' },
    { name: 'Entretien', link: '/services/entretien', icon: 'ri-tools-line' },
  ];

  return (
    <nav className={`fixed w-full z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-lg' : 'bg-transparent'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20 md:h-40">

          {/* Logo */}
          <div
            className="flex items-center cursor-pointer"
            onClick={goHome}
          >
            <img
              src="https://static.readdy.ai/image/c67d8bfa9f904100cebe9adf1f31e7a9/86cdd5776af844feb2d8fe8279d4dcc8.png"
              alt="Ion Certan - Plombier Chauffagiste Sanitaire Valence"
              className="h-16 md:h-40 w-auto"
              style={{ filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.18))' }}
            />
          </div>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-6">

            {/* Accueil */}
            <button
              onClick={goHome}
              className={`text-sm font-medium transition-colors whitespace-nowrap flex items-center gap-1 ${isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'}`}
            >
              <i className="ri-home-4-line text-base"></i>
              Accueil
            </button>

            {/* Services dropdown */}
            <div className="relative" ref={servicesRef}>
              <button
                onClick={() => setIsServicesOpen(!isServicesOpen)}
                className={`text-sm font-medium transition-colors whitespace-nowrap flex items-center gap-1 ${isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'}`}
              >
                <i className="ri-tools-line text-base"></i>
                Services
                <i className={`ri-arrow-${isServicesOpen ? 'up' : 'down'}-s-line text-base transition-transform duration-200`}></i>
              </button>

              {isServicesOpen && (
                <div className="absolute top-full left-0 mt-3 w-64 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden z-50">
                  <div className="py-2">
                    {services.map((service, index) => (
                      <a
                        key={index}
                        href={service.link}
                        onClick={() => setIsServicesOpen(false)}
                        className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-teal-50 hover:text-teal-700 transition-colors cursor-pointer"
                      >
                        <div className="w-8 h-8 flex items-center justify-center bg-teal-100 rounded-lg text-teal-600">
                          <i className={`${service.icon} text-base`}></i>
                        </div>
                        {service.name}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <button
              onClick={() => scrollToSection('a-propos')}
              className={`text-sm font-medium transition-colors whitespace-nowrap ${isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'}`}
            >
              À propos
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className={`text-sm font-medium transition-colors whitespace-nowrap ${isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'}`}
            >
              Contact
            </button>
            <a
              href="tel:+33760705475"
              className="bg-teal-600 text-white px-6 py-2.5 rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium whitespace-nowrap"
            >
              <i className="ri-phone-line mr-2"></i>
              07 60 70 54 75
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`md:hidden ${isScrolled ? 'text-gray-700' : 'text-white'}`}
          >
            <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-white shadow-lg">
          <div className="px-4 py-4 space-y-2">
            <button
              onClick={goHome}
              className="flex items-center gap-2 w-full text-left px-4 py-2 text-gray-700 hover:bg-teal-50 hover:text-teal-700 rounded-lg"
            >
              <i className="ri-home-4-line"></i>
              Accueil
            </button>

            <div>
              <button
                onClick={() => setIsMobileServicesOpen(!isMobileServicesOpen)}
                className="flex items-center justify-between w-full px-4 py-2 text-gray-700 hover:bg-teal-50 hover:text-teal-700 rounded-lg"
              >
                <span className="flex items-center gap-2">
                  <i className="ri-tools-line"></i>
                  Nos Services
                </span>
                <i className={`ri-arrow-${isMobileServicesOpen ? 'up' : 'down'}-s-line`}></i>
              </button>
              {isMobileServicesOpen && (
                <div className="ml-4 mt-1 space-y-1">
                  {services.map((service, index) => (
                    <a
                      key={index}
                      href={service.link}
                      className="flex items-center gap-2 px-4 py-2 text-sm text-gray-600 hover:bg-teal-50 hover:text-teal-700 rounded-lg"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <i className={`${service.icon} text-teal-500`}></i>
                      {service.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <button
              onClick={() => scrollToSection('a-propos')}
              className="flex items-center gap-2 w-full text-left px-4 py-2 text-gray-700 hover:bg-teal-50 hover:text-teal-700 rounded-lg"
            >
              À propos
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className="flex items-center gap-2 w-full text-left px-4 py-2 text-gray-700 hover:bg-teal-50 hover:text-teal-700 rounded-lg"
            >
              Contact
            </button>
            <a
              href="tel:+33760705475"
              className="flex items-center justify-center gap-2 w-full bg-teal-600 text-white px-6 py-2.5 rounded-lg hover:bg-teal-700 transition-colors"
            >
              <i className="ri-phone-line"></i>
              07 60 70 54 75
            </a>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
